/**
 * Chat Engine -- two-phase unified message flow with @mention routing.
 *
 * Phase 1: sendToHost -- host produces complete response (LLM loop or remote RPC)
 * Phase 2: processMentions -- post-process host response for @mentions, feed results back
 *
 * All external deps injected. Framework-agnostic.
 */

import { toLLMMessages } from 'session-manager'

const RPC_TIMEOUT = 30000 // 30s for tool/host RPC
const CHAT_TIMEOUT = 60000 // 60s for chat peer RPC

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timeout after ${ms}ms`)), ms),
    ),
  ])
}

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Parse @mentions from content.
 * Each participant must have a mentionName (no spaces, e.g. Chrome_Extension).
 */
export function parseMentions(content: string, participants: any[]) {
  if (!participants?.length) return []
  const allNames = participants.map((p: any) => escapeRegex(p.mentionName)).join('|')
  const mentions: { peer: any; message: string }[] = []
  for (const p of participants) {
    const pattern = new RegExp(
      `@${escapeRegex(p.mentionName)}\\s+([\\s\\S]+?)(?=@(?:${allNames})\\s|$)`,
      'g',
    )
    for (const match of content.matchAll(pattern)) {
      mentions.push({ peer: p, message: match[1].trim() })
    }
  }
  return mentions
}

/**
 * Build participant section for system prompt / systemPromptSuffix.
 */
export function buildParticipantPrompt(participants: any[]) {
  if (!participants?.length) return ''
  let section = 'You are in a group conversation. Available participants:\n'
  for (const p of participants) {
    section += `\n- @${p.mentionName} (chat): ${p.description || p.displayName}. To use, @mention with natural language request.`
  }
  section +=
    "\n\nWhen you need a participant's help, @mention them in your response. Their reply will appear in the next message."
  return section
}

/**
 * Create a chat engine.
 */
export function createChatEngine({ chatFn, buildPrompt, roleCall }: any) {
  /**
   * callHost -- dispatch to LLM bot or chat worker.
   * Returns { answer, usage?, modelInfo? }
   */
  async function callHost(
    host: any,
    messages: any[],
    { participantPrompt, botConfig, onStep }: any = {},
  ) {
    const nameMap = host.nameMap || {}
    const llmMessages = toLLMMessages(messages, nameMap)

    if (host.type === 'llm-bot') {
      const systemPrompt = buildPrompt(host.botConfig || botConfig, participantPrompt)
      const fullMessages = [{ role: 'system', content: systemPrompt }, ...llmMessages]
      console.log('[Chat Engine] messages:', JSON.stringify(fullMessages, null, 2))
      onStep?.('calling_llm')
      const response = await chatFn(fullMessages, host.botConfig || botConfig)
      return { answer: response.answer, usage: response.usage, modelInfo: response.modelInfo }
    }

    // Remote chat worker
    const result: any = await withTimeout(
      roleCall(host.roleId, 'chat', [
        { messages: llmMessages, systemPromptSuffix: participantPrompt || '' },
      ]),
      RPC_TIMEOUT,
      `callHost(${host.roleId})`,
    )
    if (result?.taskId) {
      return await pollUntilDone(host, result.taskId)
    }
    return { answer: result?.content || result?.answer || result?.response || '' }
  }

  /**
   * Poll async task until done.
   * Worker exposes getTaskStatus({ taskId }) -> { status, result, error, output }
   */
  async function pollUntilDone(host: any, taskId: string) {
    const POLL_INTERVAL = 2000
    const MAX_POLLS = 150 // 5 minutes

    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL))
      const result: any = await withTimeout(
        roleCall(host.roleId, 'getTaskStatus', [{ taskId }]),
        RPC_TIMEOUT,
        `getTaskStatus(${taskId})`,
      )
      if (result?.status === 'completed') {
        return { answer: result.result || '' }
      }
      if (result?.status === 'failed') throw new Error(result.error || 'Async task failed')
      if (result?.status === 'cancelled') throw new Error('Task was cancelled')
    }
    throw new Error('Async task polling timeout')
  }

  /**
   * callPeer -- dispatch to a chat peer.
   */
  async function callPeer(peer: any, message: string) {
    // Chat peer
    const result: any = await withTimeout(
      roleCall(peer.roleId, 'chat', [{ message }]),
      CHAT_TIMEOUT,
      `chat@${peer.mentionName}`,
    )
    return result?.content || result?.answer || result?.response || JSON.stringify(result)
  }

  /**
   * Phase 1: Send user message to host, wait for complete response.
   */
  async function sendToHost(
    content: string,
    { host, messages, save, botConfig, participantPrompt, onStep }: any,
  ) {
    const userMsg = { role: 'user', content, timestamp: Date.now() }
    messages.push(userMsg)
    await save(userMsg)

    onStep?.('calling_llm')
    const response = await callHost(host, messages, { participantPrompt, botConfig, onStep })
    const assistantMsg = { role: 'assistant', content: response.answer, timestamp: Date.now() }
    messages.push(assistantMsg)
    await save(assistantMsg)

    return assistantMsg
  }

  /**
   * Phase 2: Process @mentions in a message (post-processing step).
   * Depth-limited to prevent infinite loops.
   */
  async function processMentions(message: any, opts: any, depth = 0): Promise<void> {
    const { host, participants, messages, save, botConfig, participantPrompt, onStep } = opts
    if (depth >= 10) return

    const mentions = parseMentions(message.content, participants)
    if (!mentions.length) return

    // Execute each @mention sequentially
    for (const m of mentions) {
      onStep?.('calling_peer')
      let result: string
      let isError = false
      try {
        result = await callPeer(m.peer, m.message)
      } catch (err: any) {
        result = `Error from @${m.peer.mentionName}: ${err.message}`
        isError = true
      }
      const participantMsg: any = {
        role: 'participant',
        participantId: m.peer.roleId,
        content: result,
        timestamp: Date.now(),
        ...(isError && { isError: true }),
      }
      messages.push(participantMsg)
      await save(participantMsg)
    }

    // Feed all @mention results back to host for a follow-up
    onStep?.('calling_llm')
    const followUp = await callHost(host, messages, { participantPrompt, botConfig, onStep })
    const followUpMsg = { role: 'assistant', content: followUp.answer, timestamp: Date.now() }
    messages.push(followUpMsg)
    await save(followUpMsg)

    // Follow-up may contain further @mentions
    await processMentions(followUpMsg, opts, depth + 1)
  }

  /**
   * Main entry point: handle a user message end-to-end.
   */
  const handleUserMessage = async (content: string, opts: any) => {
    const { host, participants = [], messages, save, botConfig, onStep } = opts

    // Deduplicate participants by mentionName (first wins)
    const seen = new Set<string>()
    const uniqueParticipants = participants.filter((p: any) => {
      if (seen.has(p.mentionName)) return false
      seen.add(p.mentionName)
      return true
    })

    // Build nameMap for toLLMMessages: participantId -> displayName
    const nameMap: Record<string, string> = { ...host.nameMap }
    for (const p of uniqueParticipants) {
      nameMap[p.roleId] = p.displayName || p.mentionName
    }
    // Inject nameMap into host so callHost can use it for toLLMMessages
    const hostWithNames = { ...host, nameMap }

    const participantPrompt =
      uniqueParticipants.length > 0 ? buildParticipantPrompt(uniqueParticipants) : undefined
    const sharedOpts = {
      host: hostWithNames,
      participants: uniqueParticipants,
      messages,
      save,
      botConfig,
      participantPrompt,
      onStep,
    }

    // Phase 1
    const hostResponse = await sendToHost(content, sharedOpts)

    // Phase 2
    await processMentions(hostResponse, sharedOpts)
  }

  return { handleUserMessage, parseMentions, buildParticipantPrompt, callHost, callPeer }
}
